// LowCreditsWarning Component
// Displays a warning banner when credits are running low

import { FiAlertTriangle, FiX } from 'react-icons/fi';
import { Button } from '../../ui/Button/Button';
import styles from './LowCreditsWarning.module.css';

interface LowCreditsWarningProps {
  creditsRemaining: number;
  creditsLimit: number;
  onUpgrade: () => void;
  onDismiss?: () => void;
  variant?: 'warning' | 'critical';
}

export function LowCreditsWarning({
  creditsRemaining,
  creditsLimit,
  onUpgrade,
  onDismiss,
  variant = 'warning',
}: LowCreditsWarningProps) {
  const percentage = (creditsRemaining / creditsLimit) * 100;
  const isCritical = variant === 'critical' || percentage < 10;
  const isDepleted = creditsRemaining === 0;

  // Determine title based on state
  const getTitle = () => {
    if (isDepleted) return 'Critical: Credits Depleted';
    if (isCritical) return 'Critical: Credits Almost Depleted';
    return 'Low Credits Warning';
  };

  // Determine message based on state
  const getMessage = () => {
    if (isDepleted) {
      return ' Your services are currently interrupted. Upgrade now to continue using LazySEM.';
    }
    if (isCritical) {
      return ' Your services may be interrupted soon. Upgrade now to continue using LazySEM.';
    }
    return ' Consider upgrading your plan to avoid service interruption.';
  };

  return (
    <div className={`${styles.warning} ${isCritical ? styles.critical : styles.low}`}>
      <div className={styles.iconWrapper}>
        <FiAlertTriangle className={styles.icon} />
      </div>

      <div className={styles.content}>
        <h3 className={styles.title}>
          {getTitle()}
        </h3>
        <p className={styles.message}>
          You have <strong>{creditsRemaining.toLocaleString()}</strong> credits remaining (
          {percentage.toFixed(1)}% of your {creditsLimit.toLocaleString()} credit limit).
          {getMessage()}
        </p>
      </div>

      <div className={styles.actions}>
        <Button
          variant={isCritical ? 'error' : 'danger'}
          size="sm"
          onClick={onUpgrade}
        >
          Upgrade Plan
        </Button>
        {onDismiss && !isCritical && (
          <button className={styles.dismissButton} onClick={onDismiss}>
            <FiX />
          </button>
        )}
      </div>
    </div>
  );
}

